import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, getAuthToken } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Phone, Mail, User, Calendar as CalendarIcon, CheckCircle, XCircle, 
  Clock, ArrowRight, LogOut, ChevronLeft, ChevronRight, Activity,
  Eye, Briefcase, FileText, ThumbsUp, ThumbsDown, Repeat, 
  Home, BarChart3, Users, Bell, RefreshCw, Loader2, Shuffle, ToggleLeft, ToggleRight, RotateCcw
} from 'lucide-react';
import { format, parseISO, isToday, isBefore } from 'date-fns';

const TelesalesCRM = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [leads, setLeads] = useState([]);
  const [currentLeadIndex, setCurrentLeadIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [followUpDate, setFollowUpDate] = useState(null);
  const [showFollowUpPicker, setShowFollowUpPicker] = useState(false);
  const [activeView, setActiveView] = useState('leads'); // leads, followups, analytics
  const [followUpLeads, setFollowUpLeads] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [telesalesMembers, setTelesalesMembers] = useState([]);
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [autoAssignStatus, setAutoAssignStatus] = useState(null);

  // Check authentication and get user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = getAuthToken();
        if (!token) {
          navigate('/login');
          return;
        }
        
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setUser(response.data);
        
        // Only telesales and admin can access
        if (!['telesales', 'admin', 'crm_user'].includes(response.data.role) && !response.data.is_admin) {
          toast.error('Access denied. Telesales role required.');
          navigate('/dashboard');
          return;
        }
      } catch (error) {
        console.error('Auth error:', error);
        navigate('/login');
      }
    };
    
    fetchUser();
  }, [navigate]);

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter new leads (not yet actioned)
      const newLeads = response.data.filter(lead => lead.status === 'new');
      const followUps = response.data.filter(lead => lead.status === 'follow_up');
      
      setLeads(newLeads);
      setFollowUpLeads(followUps);
      setCurrentLeadIndex(0);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API}/telesales/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  }, []);

  // Fetch telesales members for admin assignment
  const fetchTelesalesMembers = useCallback(async () => {
    if (user?.role !== 'admin' && user?.role !== 'crm_user' && !user?.is_admin) return;
    
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API}/telesales/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTelesalesMembers(response.data);
    } catch (error) {
      console.error('Error fetching telesales members:', error);
    }
  }, [user]);

  // Fetch auto-assign status
  const fetchAutoAssignStatus = useCallback(async () => {
    if (user?.role !== 'admin' && user?.role !== 'crm_user' && !user?.is_admin) return;
    
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API}/telesales/auto-assign/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutoAssignStatus(response.data);
    } catch (error) {
      console.error('Error fetching auto-assign status:', error);
    }
  }, [user]);

  // Toggle auto-assign
  const toggleAutoAssign = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.patch(`${API}/telesales/auto-assign/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchAutoAssignStatus();
    } catch (error) {
      toast.error('Failed to toggle auto-assign');
    }
  };

  // Reset round-robin
  const resetRoundRobin = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.post(`${API}/telesales/auto-assign/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchAutoAssignStatus();
    } catch (error) {
      toast.error('Failed to reset round-robin');
    }
  };

  useEffect(() => {
    if (user) {
      fetchLeads();
      fetchAnalytics();
      fetchTelesalesMembers();
      fetchAutoAssignStatus();
    }
  }, [user, fetchLeads, fetchAnalytics, fetchTelesalesMembers, fetchAutoAssignStatus]);

  const currentLead = leads[currentLeadIndex];

  // Click to Call
  const handleCall = () => {
    if (currentLead?.phone) {
      window.location.href = `tel:${currentLead.phone}`;
      toast.success('Opening phone dialer...');
    } else {
      toast.error('No phone number available');
    }
  };

  // Update lead status
  const updateLeadStatus = async (status) => {
    if (!currentLead) return;
    
    setActionLoading(true);
    try {
      const token = getAuthToken();
      const payload = {
        status,
        feedback
      };
      
      // Only include follow_up_date if it's set
      if (followUpDate) {
        payload.follow_up_date = format(followUpDate, 'yyyy-MM-dd');
      }
      
      await axios.patch(`${API}/leads/${currentLead.id}/status`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Lead marked as ${status.replace('_', ' ')}`);
      
      // Reset form
      setFeedback('');
      setFollowUpDate(null);
      setShowFollowUpPicker(false);
      
      // Move to next lead
      if (currentLeadIndex < leads.length - 1) {
        setCurrentLeadIndex(prev => prev + 1);
      } else {
        // Refresh leads list
        await fetchLeads();
      }
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Failed to update lead status');
    } finally {
      setActionLoading(false);
    }
  };

  // Assign lead to telesales member
  const assignLead = async (memberEmail) => {
    if (!currentLead) return;
    
    try {
      const token = getAuthToken();
      await axios.patch(`${API}/leads/${currentLead.id}/assign?assigned_to=${memberEmail}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Lead assigned successfully');
      setShowAssignDropdown(false);
      await fetchLeads();
    } catch (error) {
      console.error('Error assigning lead:', error);
      toast.error('Failed to assign lead');
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return format(parseISO(dateStr), 'MMM dd, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  // Get activity icon
  const getActivityIcon = (type) => {
    switch (type) {
      case 'job_view': return <Briefcase className="h-4 w-4" />;
      case 'course_visit': return <FileText className="h-4 w-4" />;
      case 'demo_watch': return <Eye className="h-4 w-4" />;
      case 'job_apply': return <CheckCircle className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  // Navigate to main dashboard
  const goToDashboard = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-purple-400 mx-auto mb-4" />
          <p className="text-white/70">Loading Telesales CRM...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900" data-testid="telesales-crm-page">
      {/* Header */}
      <header className="bg-black/30 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Phone className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Telesales CRM</h1>
              <p className="text-xs text-white/60">Officium Connect</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* View Toggles */}
            <div className="bg-white/10 rounded-xl p-1 flex gap-1">
              <Button
                variant={activeView === 'leads' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveView('leads')}
                className={activeView === 'leads' ? 'bg-purple-500' : 'text-white/70 hover:text-white hover:bg-white/10'}
                data-testid="view-leads-btn"
              >
                <Users className="h-4 w-4 mr-1" />
                Leads ({leads.length})
              </Button>
              <Button
                variant={activeView === 'followups' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveView('followups')}
                className={activeView === 'followups' ? 'bg-orange-500' : 'text-white/70 hover:text-white hover:bg-white/10'}
                data-testid="view-followups-btn"
              >
                <Bell className="h-4 w-4 mr-1" />
                Follow-ups ({followUpLeads.length})
              </Button>
              <Button
                variant={activeView === 'analytics' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveView('analytics')}
                className={activeView === 'analytics' ? 'bg-emerald-500' : 'text-white/70 hover:text-white hover:bg-white/10'}
                data-testid="view-analytics-btn"
              >
                <BarChart3 className="h-4 w-4 mr-1" />
                Analytics
              </Button>
            </div>
            
            {/* Dashboard button - only for admin/crm_user (not pure telesales) */}
            {(user?.is_admin || user?.role === 'admin' || user?.role === 'crm_user') && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={goToDashboard}
                className="text-white/70 hover:text-white hover:bg-white/10"
                data-testid="go-to-dashboard-btn"
              >
                <Home className="h-4 w-4 mr-1" />
                Dashboard
              </Button>
            )}
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-white/70 hover:text-white hover:bg-white/10"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* LEADS VIEW - One at a time */}
        {activeView === 'leads' && (
          <>
            {leads.length === 0 ? (
              <Card className="bg-white/10 border-white/20 backdrop-blur-xl">
                <CardContent className="py-16 text-center">
                  <CheckCircle className="h-16 w-16 text-emerald-400 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-white mb-2">All Caught Up!</h2>
                  <p className="text-white/60 mb-4">No new leads to process. Great work!</p>
                  <Button onClick={fetchLeads} className="bg-purple-500 hover:bg-purple-600" data-testid="refresh-leads-btn">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh Leads
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Main Lead Card */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Progress Bar */}
                  <div className="flex items-center gap-4 text-white/70 text-sm">
                    <span>Lead {currentLeadIndex + 1} of {leads.length}</span>
                    <div className="flex-1 bg-white/10 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${((currentLeadIndex + 1) / leads.length) * 100}%` }}
                      />
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCurrentLeadIndex(Math.max(0, currentLeadIndex - 1))}
                        disabled={currentLeadIndex === 0}
                        className="text-white/70 hover:text-white"
                        data-testid="prev-lead-btn"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCurrentLeadIndex(Math.min(leads.length - 1, currentLeadIndex + 1))}
                        disabled={currentLeadIndex === leads.length - 1}
                        className="text-white/70 hover:text-white"
                        data-testid="next-lead-btn"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Lead Info Card */}
                  <Card className="bg-white/10 border-white/20 backdrop-blur-xl overflow-hidden" data-testid="current-lead-card">
                    <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 p-6 border-b border-white/10">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          <div className="h-16 w-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center text-2xl font-bold text-white">
                            {currentLead?.name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <h2 className="text-2xl font-bold text-white" data-testid="lead-name">{currentLead?.name || 'Unknown'}</h2>
                            <div className="flex items-center gap-3 mt-1">
                              <Badge variant="outline" className="border-purple-400/50 text-purple-300">
                                {currentLead?.source || 'Direct'}
                              </Badge>
                              <span className="text-white/50 text-sm">
                                {formatDate(currentLead?.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Admin Assignment */}
                        {(user?.role === 'admin' || user?.role === 'crm_user' || user?.is_admin) && (
                          <Popover open={showAssignDropdown} onOpenChange={setShowAssignDropdown}>
                            <PopoverTrigger asChild>
                              <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10" data-testid="assign-lead-btn">
                                <Users className="h-4 w-4 mr-1" />
                                Assign
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-48 bg-slate-800 border-slate-700 p-2">
                              <div className="space-y-1">
                                {telesalesMembers.length === 0 ? (
                                  <p className="text-sm text-white/60 p-2">No telesales members</p>
                                ) : (
                                  telesalesMembers.map(member => (
                                    <Button
                                      key={member.id}
                                      variant="ghost"
                                      size="sm"
                                      className="w-full justify-start text-white hover:bg-white/10"
                                      onClick={() => assignLead(member.email)}
                                      data-testid={`assign-to-${member.email}`}
                                    >
                                      {member.name}
                                    </Button>
                                  ))
                                )}
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                    </div>

                    <CardContent className="p-6 space-y-6">
                      {/* Contact Info */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-white/5 rounded-xl p-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                              <Mail className="h-5 w-5 text-blue-400" />
                            </div>
                            <div>
                              <p className="text-xs text-white/50">Email</p>
                              <p className="text-white font-medium" data-testid="lead-email">{currentLead?.email || 'N/A'}</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white/5 rounded-xl p-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                              <Phone className="h-5 w-5 text-green-400" />
                            </div>
                            <div>
                              <p className="text-xs text-white/50">Phone</p>
                              <p className="text-white font-medium" data-testid="lead-phone">{currentLead?.phone || 'N/A'}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Interest Type */}
                      {currentLead?.interest_type && (
                        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-4">
                          <p className="text-xs text-amber-400/70 mb-1">Interested In</p>
                          <p className="text-white font-medium">{currentLead.interest_type}</p>
                        </div>
                      )}

                      {/* Click to Call Button */}
                      <Button
                        onClick={handleCall}
                        size="lg"
                        className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white h-14 text-lg font-semibold"
                        data-testid="click-to-call-btn"
                      >
                        <Phone className="h-6 w-6 mr-2" />
                        Click to Call
                      </Button>

                      {/* Feedback Textarea */}
                      <div>
                        <label className="text-sm text-white/70 mb-2 block">Call Notes / Feedback</label>
                        <Textarea
                          value={feedback}
                          onChange={(e) => setFeedback(e.target.value)}
                          placeholder="Add notes about the call..."
                          className="bg-white/5 border-white/20 text-white placeholder:text-white/40 min-h-[100px]"
                          data-testid="feedback-textarea"
                        />
                      </div>

                      {/* Follow-up Date Picker */}
                      {showFollowUpPicker && (
                        <div className="bg-white/5 rounded-xl p-4">
                          <label className="text-sm text-white/70 mb-2 block">Select Follow-up Date</label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start border-white/20 text-white hover:bg-white/10" data-testid="followup-date-trigger">
                                <CalendarIcon className="h-4 w-4 mr-2" />
                                {followUpDate ? format(followUpDate, 'PPP') : 'Pick a date'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-slate-800 border-slate-700">
                              <Calendar
                                mode="single"
                                selected={followUpDate}
                                onSelect={setFollowUpDate}
                                disabled={(date) => isBefore(date, new Date())}
                                className="bg-slate-800 text-white"
                                data-testid="followup-calendar"
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="grid grid-cols-3 gap-3">
                        <Button
                          onClick={() => updateLeadStatus('interested')}
                          disabled={actionLoading}
                          className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white h-14 flex flex-col items-center justify-center"
                          data-testid="mark-interested-btn"
                        >
                          {actionLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                            <>
                              <ThumbsUp className="h-5 w-5 mb-1" />
                              <span className="text-xs">Interested</span>
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => updateLeadStatus('not_interested')}
                          disabled={actionLoading}
                          className="bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white h-14 flex flex-col items-center justify-center"
                          data-testid="mark-not-interested-btn"
                        >
                          {actionLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                            <>
                              <ThumbsDown className="h-5 w-5 mb-1" />
                              <span className="text-xs">Not Interested</span>
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => {
                            if (!showFollowUpPicker) {
                              setShowFollowUpPicker(true);
                            } else if (followUpDate) {
                              updateLeadStatus('follow_up');
                            } else {
                              toast.error('Please select a follow-up date');
                            }
                          }}
                          disabled={actionLoading}
                          className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white h-14 flex flex-col items-center justify-center"
                          data-testid="mark-followup-btn"
                        >
                          {actionLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                            <>
                              <Repeat className="h-5 w-5 mb-1" />
                              <span className="text-xs">Follow-up</span>
                            </>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Activity Sidebar */}
                <div className="space-y-6">
                  <Card className="bg-white/10 border-white/20 backdrop-blur-xl">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-white flex items-center gap-2">
                        <Activity className="h-5 w-5 text-purple-400" />
                        Activity History
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 max-h-[500px] overflow-y-auto">
                      {currentLead?.interactions?.length > 0 ? (
                        currentLead.interactions.map((interaction, idx) => (
                          <div key={idx} className="bg-white/5 rounded-lg p-3">
                            <div className="flex items-start gap-3">
                              <div className="h-8 w-8 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400">
                                {getActivityIcon(interaction.interaction_type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-white text-sm font-medium capitalize">
                                  {interaction.interaction_type?.replace('_', ' ') || 'Activity'}
                                </p>
                                <p className="text-white/50 text-xs truncate">
                                  {interaction.details?.title || interaction.page || 'Unknown'}
                                </p>
                                <p className="text-white/40 text-xs mt-1">
                                  {formatDate(interaction.timestamp)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-white/50 text-sm text-center py-4">No activity recorded</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Quick Stats */}
                  <Card className="bg-white/10 border-white/20 backdrop-blur-xl">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-white text-sm">Today's Progress</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-2 gap-3">
                      <div className="bg-emerald-500/20 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-emerald-400">{analytics?.interested || 0}</p>
                        <p className="text-xs text-white/60">Interested</p>
                      </div>
                      <div className="bg-red-500/20 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-red-400">{analytics?.not_interested || 0}</p>
                        <p className="text-xs text-white/60">Not Interested</p>
                      </div>
                      <div className="bg-amber-500/20 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-amber-400">{analytics?.follow_up || 0}</p>
                        <p className="text-xs text-white/60">Follow-ups</p>
                      </div>
                      <div className="bg-purple-500/20 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-purple-400">{analytics?.total || 0}</p>
                        <p className="text-xs text-white/60">Total Leads</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </>
        )}

        {/* FOLLOW-UPS VIEW */}
        {activeView === 'followups' && (
          <div className="space-y-6">
            <Card className="bg-white/10 border-white/20 backdrop-blur-xl">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Bell className="h-5 w-5 text-orange-400" />
                  Follow-up Leads
                </CardTitle>
                <CardDescription className="text-white/60">
                  Leads scheduled for follow-up calls
                </CardDescription>
              </CardHeader>
              <CardContent>
                {followUpLeads.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
                    <p className="text-white/70">No follow-ups scheduled</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {followUpLeads.map((lead) => {
                      const isOverdue = lead.follow_up_date && isBefore(parseISO(lead.follow_up_date), new Date()) && !isToday(parseISO(lead.follow_up_date));
                      const isDueToday = lead.follow_up_date && isToday(parseISO(lead.follow_up_date));
                      
                      return (
                        <div 
                          key={lead.id} 
                          className={`bg-white/5 rounded-xl p-4 border ${isOverdue ? 'border-red-500/50' : isDueToday ? 'border-amber-500/50' : 'border-white/10'}`}
                          data-testid={`followup-lead-${lead.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="h-12 w-12 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center text-lg font-bold text-white">
                                {lead.name?.charAt(0) || '?'}
                              </div>
                              <div>
                                <h3 className="text-white font-semibold">{lead.name}</h3>
                                <p className="text-white/50 text-sm">{lead.phone}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <Badge variant={isOverdue ? 'destructive' : isDueToday ? 'warning' : 'secondary'} className={isOverdue ? 'bg-red-500' : isDueToday ? 'bg-amber-500' : ''}>
                                  {isOverdue ? 'Overdue' : isDueToday ? 'Due Today' : 'Scheduled'}
                                </Badge>
                                <p className="text-white/50 text-xs mt-1">
                                  {lead.follow_up_date ? format(parseISO(lead.follow_up_date), 'MMM dd, yyyy') : 'No date'}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => window.location.href = `tel:${lead.phone}`}
                                className="bg-green-500 hover:bg-green-600"
                                data-testid={`call-followup-${lead.id}`}
                              >
                                <Phone className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          {lead.feedback && (
                            <p className="text-white/60 text-sm mt-3 bg-white/5 rounded-lg p-2">
                              <span className="text-white/40">Notes:</span> {lead.feedback}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ANALYTICS VIEW */}
        {activeView === 'analytics' && analytics && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border-blue-500/30">
                <CardContent className="p-6 text-center">
                  <p className="text-4xl font-bold text-blue-400">{analytics.total || 0}</p>
                  <p className="text-white/70 mt-1">Total Leads</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500/30">
                <CardContent className="p-6 text-center">
                  <p className="text-4xl font-bold text-purple-400">{analytics.new || 0}</p>
                  <p className="text-white/70 mt-1">New Leads</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-emerald-500/20 to-green-500/20 border-emerald-500/30">
                <CardContent className="p-6 text-center">
                  <p className="text-4xl font-bold text-emerald-400">{analytics.interested || 0}</p>
                  <p className="text-white/70 mt-1">Interested</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-500/30">
                <CardContent className="p-6 text-center">
                  <p className="text-4xl font-bold text-amber-400">{analytics.follow_up || 0}</p>
                  <p className="text-white/70 mt-1">Follow-ups</p>
                </CardContent>
              </Card>
            </div>

            {/* Conversion Rate */}
            <Card className="bg-white/10 border-white/20 backdrop-blur-xl">
              <CardHeader>
                <CardTitle className="text-white">Conversion Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <p className="text-5xl font-bold text-emerald-400">
                      {analytics.total > 0 ? ((analytics.interested / analytics.total) * 100).toFixed(1) : 0}%
                    </p>
                    <p className="text-white/70 mt-2">Interest Rate</p>
                  </div>
                  <div className="text-center">
                    <p className="text-5xl font-bold text-amber-400">
                      {analytics.total > 0 ? ((analytics.follow_up / analytics.total) * 100).toFixed(1) : 0}%
                    </p>
                    <p className="text-white/70 mt-2">Follow-up Rate</p>
                  </div>
                  <div className="text-center">
                    <p className="text-5xl font-bold text-purple-400">
                      {analytics.converted || 0}
                    </p>
                    <p className="text-white/70 mt-2">Converted</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Source Breakdown */}
            {analytics.source_breakdown && analytics.source_breakdown.length > 0 && (
              <Card className="bg-white/10 border-white/20 backdrop-blur-xl">
                <CardHeader>
                  <CardTitle className="text-white">Leads by Source</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analytics.source_breakdown.map((source, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                        <span className="text-white capitalize">{source._id || 'Direct'}</span>
                        <Badge className="bg-purple-500">{source.count}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Member Performance (Admin only) */}
            {analytics.member_performance && analytics.member_performance.length > 0 && (user?.role === 'admin' || user?.is_admin) && (
              <Card className="bg-white/10 border-white/20 backdrop-blur-xl">
                <CardHeader>
                  <CardTitle className="text-white">Team Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analytics.member_performance.map((member, idx) => (
                      <div key={idx} className="bg-white/5 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white font-medium">{member._id}</span>
                          <span className="text-white/70">Total: {member.total}</span>
                        </div>
                        <div className="flex gap-2">
                          <Badge className="bg-emerald-500">Interested: {member.interested}</Badge>
                          <Badge className="bg-red-500">Not Interested: {member.not_interested}</Badge>
                          <Badge className="bg-amber-500">Follow-up: {member.follow_up}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Auto-Assign Round Robin Settings (Admin only) */}
            {(user?.role === 'admin' || user?.role === 'crm_user' || user?.is_admin) && autoAssignStatus && (
              <Card className="bg-white/10 border-white/20 backdrop-blur-xl">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Shuffle className="h-5 w-5" />
                      Auto-Assign (Round Robin)
                    </CardTitle>
                    <Badge className={autoAssignStatus.enabled ? 'bg-emerald-500' : 'bg-gray-500'}>
                      {autoAssignStatus.enabled ? 'Active' : 'Disabled'}
                    </Badge>
                  </div>
                  <CardDescription className="text-white/60">
                    Automatically distribute new leads among telesales team members
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Toggle and Reset Buttons */}
                  <div className="flex gap-3">
                    <Button 
                      onClick={toggleAutoAssign}
                      variant={autoAssignStatus.enabled ? 'outline' : 'default'}
                      className={autoAssignStatus.enabled 
                        ? 'border-white/30 text-white hover:bg-white/10' 
                        : 'bg-emerald-500 hover:bg-emerald-600'}
                    >
                      {autoAssignStatus.enabled ? (
                        <><ToggleRight className="h-4 w-4 mr-2" /> Disable Auto-Assign</>
                      ) : (
                        <><ToggleLeft className="h-4 w-4 mr-2" /> Enable Auto-Assign</>
                      )}
                    </Button>
                    <Button 
                      onClick={resetRoundRobin}
                      variant="outline"
                      className="border-white/30 text-white hover:bg-white/10"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" /> Reset Rotation
                    </Button>
                  </div>

                  {/* Last Assignment Info */}
                  {autoAssignStatus.last_assigned_to && (
                    <div className="bg-white/5 rounded-lg p-3">
                      <p className="text-white/70 text-sm">Last assigned to:</p>
                      <p className="text-white font-medium">{autoAssignStatus.last_assigned_to}</p>
                      {autoAssignStatus.last_assigned_at && (
                        <p className="text-white/50 text-xs mt-1">
                          {format(parseISO(autoAssignStatus.last_assigned_at), 'MMM d, yyyy h:mm a')}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Team Distribution */}
                  <div>
                    <h4 className="text-white/80 text-sm font-medium mb-3">Lead Distribution by Team Member</h4>
                    <div className="space-y-2">
                      {autoAssignStatus.telesales_members?.map((member, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-sm font-bold">
                              {(member.name || member.email).charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-white font-medium">{member.name || member.email}</p>
                              <p className="text-white/50 text-xs">{member.email}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Badge className="bg-blue-500">{member.lead_count} total</Badge>
                            <Badge className="bg-purple-500">{member.new_leads} new</Badge>
                          </div>
                        </div>
                      ))}
                      {(!autoAssignStatus.telesales_members || autoAssignStatus.telesales_members.length === 0) && (
                        <p className="text-white/50 text-center py-4">No telesales members configured</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default TelesalesCRM;
